/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import { Sensor } from '@ouster/js-osf';
import {
  COLOR_MODE,
  DataMessage,
  operators,
  ChannelFieldRGBAData,
  ImageDestaggerer,
  ImageColorer,
  PointcloudNoiseSuppressor,
  PointcloudColorer,
  PointcloudScaler,
  PointcloudAutoLeveler,
  RangeRGBData,
  ReturnColor,
  ImageNoiseSuppressor,
  ImageAutoleveler,
  ImageScaler,
  CHANNEL_FIELD,
} from '@ouster/webviz';
import { PointsDataMessage } from '@ouster/webviz/build/src/utils/profiles';
import {
  ColorPaletteName, // as OriginalColorPaletteName,
  PointCloudGlobalSettings,
  PointCloudSensorsSettings,
  ImageSettings,
} from '@ouster/webviz-react-components';

import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  Computed,
  computed,
} from 'easy-peasy';
import { isUndefined } from 'lodash';
import type { Observable, OperatorFunction } from 'rxjs';
import {
  Subject,
  NEVER,
  map,
  share,
  shareReplay,
  catchError,
  of,
  from,
  mergeMap,
  observeOn,
  animationFrameScheduler,
  filter,
  takeUntil,
  toArray,
  take,
} from 'rxjs';

import {
  CameraPreset,
  ImageReturnsCombination,
  isColorMode,
  PointShape,
} from '../types';
import {
  allColorPalettes,
  allColorModes,
  twoDImageOptionsToAdd as twoDImageOptionsToAddBase,
  EXCLUDED_COLOR_MODES,
  EXCLUDED_IMAGE_COLOR_MODES,
} from '../utils/constants';
import { mod } from '../utils/math';
import {
  copyDataMessage,
  extractLidarProfileFromMetadata,
  getAvailableReturns,
  getPointCloudSensorsSettings,
  imageAspectRatio,
  PROFILES,
} from '../utils/misc';

export const frameCache: { lastDataMessage: DataMessage[] | null } = {
  lastDataMessage: null,
};

/// ///////////////////////////////////////////////////////////////////////////
// Constants and helpers
/// ///////////////////////////////////////////////////////////////////////////
const MIN_POINT_SIZE = 1;
const DEFAULT_POINT_SIZE = 2;
const MAX_POINT_SIZE = 50;
// Increments should be integers because at the moent webviz does not
// seem to have anti-aliasing turned on for points (would have to look
// into GL_POINT_SMOOTH to do enable it)
const POINT_SIZE_INCREMENT = 1;

const SIDE_CAM_DISTANCE = 40;
const SIDE_CAM_HEIGHT = 5;
const EPSILON = 1e-5;

const CHANNEL_ORDER = [
  'RGB',
  'REFLECTIVITY',
  'REFLECTIVITY2',
  'NEAR_IR',
  'RANGE',
  'RANGE2',
  'SIGNAL',
  'SIGNAL2',
];

const sortChannels = (channels: string[]): string[] => {
  const preferredSet = new Set(CHANNEL_ORDER);

  const preferred = channels
    .filter((ch) => preferredSet.has(ch))
    .sort((a, b) => CHANNEL_ORDER.indexOf(a) - CHANNEL_ORDER.indexOf(b));

  const custom = channels.filter((ch) => !preferredSet.has(ch)).sort();

  return [...preferred, ...custom];
};

export const CAMERA_PRESETS: CameraPreset[] = [
  {
    // Move the camera slightly to the back so that the up angle can
    // stay aligned with the z axis
    pose: { position: [-1 * EPSILON, 0, 30], orbitTarget: [0, 0, 0] },
    name: 'Top',
  },
  {
    // Move the camera slightly away from the origin and keep the orbit
    // target to 0 so that using the left mouse click will rotate the
    // camera around the sensor origin
    pose: { position: [-1 * EPSILON, 0, 0], orbitTarget: [0, 0, 0] },
    name: 'First Person',
  },
  {
    pose: {
      position: [-1 * SIDE_CAM_DISTANCE, 0, SIDE_CAM_HEIGHT],
      orbitTarget: [0, 0, 0],
    },
    name: 'Back',
  },
  {
    pose: {
      position: [SIDE_CAM_DISTANCE, 0, SIDE_CAM_HEIGHT],
      orbitTarget: [0, 0, 0],
    },
    name: 'Front',
  },
  {
    pose: {
      position: [0, SIDE_CAM_DISTANCE, SIDE_CAM_HEIGHT],
      orbitTarget: [0, 0, 0],
    },
    name: 'Left',
  },
  {
    pose: {
      position: [0, -1 * SIDE_CAM_DISTANCE, SIDE_CAM_HEIGHT],
      orbitTarget: [0, 0, 0],
    },
    name: 'Right',
  },
  {
    pose: { position: [-40, 30, 30], orbitTarget: [0, 0, 0] },
    name: '3/4',
  },
];

const DEFAULT_POINT_COLOR_MODES = [COLOR_MODE.REFLECTIVITY, COLOR_MODE.SIGNAL];
export const DEFAULT_IMAGE_COLOR_MODES = [
  COLOR_MODE.SIGNAL,
  COLOR_MODE.REFLECTIVITY,
];
const DEFAULT_IMAGE_COLOR_PALETTES = ['GREY'];
const DEFAULT_POINT_COLOR_PALETTES = ['SPZ'];
const CALIBRATED_REFLECTIVY_PALETTE = 'CALREF';

const SINGLE_RETURN_IMAGE_COMBINATIONS = [ImageReturnsCombination.return1Only];

export function extractDefaultColorSetting<Type>(
  allColorSettings: Type[],
  defaultSettingValues: Type[],
  errorMsgIfNotLoaded: string
) {
  if (allColorSettings.length === 0) {
    throw new Error(errorMsgIfNotLoaded);
  }

  let defaultSetting = allColorSettings[0];
  // eslint-disable-next-line no-restricted-syntax
  for (const setting of defaultSettingValues) {
    if (allColorSettings.includes(setting)) {
      defaultSetting = setting;
      break;
    }
  }
  return defaultSetting;
}

function nextColorSetting<Type>(
  allColorSettings: Type[],
  currentColorSetting: Type | undefined,
  errorMsgIfNotLoaded: string
) {
  if (allColorSettings.length === 0 || !currentColorSetting) {
    throw new Error(errorMsgIfNotLoaded);
  }

  const oldSettingIndex = allColorSettings.indexOf(currentColorSetting);
  if (oldSettingIndex === -1) {
    throw new Error(
      `The current color setting ${currentColorSetting} is not valid. Cannot find the next one.`
    );
  }

  const n = allColorSettings.length;
  const nextSetting = allColorSettings[mod(oldSettingIndex + 1, n)];

  return nextSetting;
}

function filterDataMessagesBySensorSN(
  targetSn: string | null
): OperatorFunction<DataMessage, DataMessage> {
  return filter(
    (message: DataMessage) => !targetSn || message?.sensorInfo?.sn === targetSn
  );
}

// TODO (mw) Bring back color by sensor?
const COLOR_MODE_NAMES: Map<COLOR_MODE | string, string> = new Map([
  [COLOR_MODE.SIGNAL, 'Signal'],
  [COLOR_MODE.REFLECTIVITY, 'Reflectivity'],
  [COLOR_MODE.NEAR_IR, 'Near Infrared'],
  [COLOR_MODE.RANGE, 'Range'],
  // [COLOR_MODE.SENSOR, 'Sensor N°'],
]);

const PALETTE_NAMES: Map<string, string> = new Map([
  ['GREY', 'Greyscale'],
  ['GREEN', 'Viridis'],
  ['RED', 'Magma'],
  ['SPZ', 'Ouster'],
  ['RBOW', 'Rainbow'],
  ['CALREF', 'Cal. Ref'],
]);

const imageColorerCache: { [key: string]: ImageColorer } = {};
// --- Add Caches for Point Cloud Returns ---
let cachedSensorReturnNumbers: Record<string, number[]> = {};
let cachedColorerReturns: Record<string, ReturnColor[]> = {};

function getCachedImageColorer(
  coloringMode: COLOR_MODE | string,
  colorPalette: string,
  whichReturns: number[] = [],
  isRgb = false
): ImageColorer {
  const key = `${coloringMode}-${colorPalette}-${whichReturns.join(',')}`;
  if (!imageColorerCache[key]) {
    imageColorerCache[key] = new ImageColorer(
      coloringMode,
      allColorPalettes[colorPalette as keyof typeof allColorPalettes],
      isRgb
    );
    // imageColorerCache[key].whichReturns = whichReturns;
  }
  return imageColorerCache[key];
}

/**
 *
 * @param colorMode
 */
export function colorModeName(colorMode: COLOR_MODE | string) {
  return COLOR_MODE_NAMES.get(colorMode) || colorMode;
}

/**
 *
 * @param paletteName
 */
export function colorPaletteName(paletteName: string) {
  return (
    PALETTE_NAMES.get(paletteName) ||
    paletteName.charAt(0).toUpperCase() + paletteName.slice(1).toLowerCase()
  );
}

/// ///////////////////////////////////////////////////////////////////////////
// State and Model
/// ///////////////////////////////////////////////////////////////////////////

// Unification: Duplicate from studio
type ShowTwoDImagePanel = boolean;
type ShowTwoDImageRulers = boolean;

export type TwoDImageState = {
  imageSettings: Record<string, ImageSettings[]>;
  showImagePanel: ShowTwoDImagePanel;
  showImageRulers: ShowTwoDImageRulers;
  imageInfo: {
    [sensorName: string]: {
      aspectRatio: number;
      dimensions: [number, number];
    };
  };
  imageObservables: Observable<ChannelFieldRGBAData>[];
  selectedImagePanelSn: string | null;
  // we need to clear the image observable when a different sensor is selected
  imageObservableDestroy$?: Subject<void>;
};

export type State = {
  // viz error
  hasVizError: boolean;
  vizErrorMessage: string;

  metadata: any; // todo type - Should be SensorMetadata[]
  pointCloudSensorsSettings: PointCloudSensorsSettings;
  pointCloudGlobalSettings: PointCloudGlobalSettings;

  // Pointcloud operators
  pcScaler: PointcloudScaler;
  pcAutoLeveler: PointcloudAutoLeveler;
  pcNoiseSuppressor: PointcloudNoiseSuppressor;
  pcColorers: Record<string, PointcloudColorer>;

  twoDImageSettings: TwoDImageState;

  // Image operators
  imageDestaggerer: ImageDestaggerer;
  imageScaler: ImageScaler;
  imageSuppressor: ImageNoiseSuppressor;
  imageAutoLeveler: ImageAutoleveler;
  imageColorers: ImageColorer[];

  // We do not need a pause stream emitter for replayed data
  // since we don't handle live data like in studio
  dataMessage: Subject<DataMessage[]>;
  pointCloudObservable: Observable<RangeRGBData[][]>;
  imageObservable: Observable<DataMessage>;

  availableChannels: (CHANNEL_FIELD | string)[];

  rgbChannels: string[];

  // INDEPENDENT OF STUDIO:
  // Cameras - camera orthographic setting moved to pointcloudGlobalSetting
  cameraPreset: CameraPreset;
  // If the user rotates/pans with their mouse, the camera view no
  // longer matches the selected cameraPreset, and we consider the
  // camera to be 'dirty'
  isCameraDirty: boolean;

  // Point Appearance
  pointShape: PointShape;
  pointSize: number;

  // TODO(pierre) on 2021-11-24: Maybe have one thunk which awaits for
  // the init to load and then sets everything at once to improve
  // performance?

  // Color Modes
  availablePointColorModes: (COLOR_MODE | string)[];
  pointColorMode?: COLOR_MODE | string;
  availableImageColorModes: (COLOR_MODE | string)[];
  imageColorMode?: COLOR_MODE | string;
  autoLevelImage: boolean;
  autoLevelPointCloud: boolean;
  nearIRNoiseSuppressed: boolean;

  // Color Palettes
  colorPalettes: string[];
  pointColorPalette?: string;
  previousPointColorPalette?: string; // Needed when switching from calref to another colorMode
  imageColorPalette?: string;
  previousImageColorPalette?: string; // Needed when switching from calref to another colorMode
  hasCalibratedReflectivity?: boolean;

  modeSpecificPalettes: { [key in COLOR_MODE]?: ColorPaletteName };

  // 2D Image State
  is2DImageVisible: boolean;

  // Returns
  availableImageReturnsCombination: ImageReturnsCombination[];

  imageReturnsCombination: ImageReturnsCombination;
  previousImageReturnsCombination?: ImageReturnsCombination;

  // Range Rings
  areRingsVisible: boolean;

  // sidebars
  // Unification sidebar
  leftSidebarExpanded: boolean;
  flipped: boolean;
};

export const defaultPointCloudSensorsSettings: PointCloudSensorsSettings = {};

export const defaultPointCloudGlobalSettings: PointCloudGlobalSettings = {
  coloringMode: COLOR_MODE.REFLECTIVITY,
  colorPalette: 'CalRef',
  colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
  colorModeOptions: [],
  isAutoLevelsOn: false,
  isAutoLevelsDisabled: true,
  isNoiseSuppressionOn: false,
  isNoiseSuppressionDisabled: true, // Keep pc noise suppression disabled for preliminary unification
  showPointCloud: true,
  pointSize: DEFAULT_POINT_SIZE,
  showRangeRings: true,
  isOrthographicCamera: false,
};

const defaultImageSettings: ImageSettings = {
  showImage: true,
  coloringMode: COLOR_MODE.SIGNAL,
  colorPalette: 'Greyscale',
  colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
  colorModeOptions: [COLOR_MODE.SIGNAL, COLOR_MODE.REFLECTIVITY],
  isAutoLevelsDisabled: false,
  isAutoLevelsOn: true,
  isNoiseSuppressionDisabled: true,
  isNoiseSuppressionOn: false,
  returnsSettings: [{ visible: true, disabled: false }],
};

// TODO (mw) Will need to check data profile for this ultimately
export const defaultTwoDImageSettings: TwoDImageState = {
  imageSettings: {},
  showImagePanel: true,
  showImageRulers: true, // unify these
  imageInfo: {},
  imageObservables: [],
  selectedImagePanelSn: null,
};

/**
 * Calculates dynamic 2D image options based on sensor profile.
 * Includes dynamically calculated returnsSettings.
 */
const calculateDynamicImageOptions = (
  udpProfileLidar: string | undefined,
  baseOptions: Record<string, Partial<ImageSettings>>,
  actualChannels: (CHANNEL_FIELD | string)[] = []
): Record<COLOR_MODE | string, ImageSettings> => {
  if (!udpProfileLidar) {
    console.warn(
      'Cannot calculate dynamic image options: udpProfileLidar is missing.'
    );
    return Object.entries(baseOptions).reduce((acc, [key, value]) => {
      const mode = key;
      acc[mode] = {
        ...defaultImageSettings,
        ...value,
        coloringMode: mode,
        returnsSettings:
          value.returnsSettings ?? defaultImageSettings.returnsSettings,
      };
      return acc;
    }, {} as Record<COLOR_MODE | string, ImageSettings>);
  }

  return Object.values(baseOptions).reduce(
    (
      acc,
      { returnsSettings: baseReturnSettings, coloringMode, ...imageOption }
    ) => {
      // Ensure we have a valid coloringMode key from baseOptions before proceeding
      if (coloringMode) {
        let availableReturns: { visible: boolean; disabled: boolean }[] = [];
        try {
          // Now coloringMode is guaranteed to be defined here
          availableReturns = getAvailableReturns({
            colorMode: coloringMode,
            actualChannels,
          }).map((_, index) => ({ visible: index === 0, disabled: false }));
          if (availableReturns.length === 1)
            availableReturns[0].disabled = true;
        } catch (error) {
          console.error(
            `Error getting available returns for ${coloringMode}:`,
            error
          );
          availableReturns = []; // Default to empty on error
        }

        // Construct the setting object
        const isReflectivity =
          typeof coloringMode === 'string' &&
          coloringMode.startsWith(COLOR_MODE.REFLECTIVITY);

        acc[coloringMode] = {
          ...defaultImageSettings,
          ...imageOption,
          coloringMode,
          // Set initial palette to CalRef for Reflectivity modes
          colorPalette: isReflectivity
            ? ('CalRef' as ColorPaletteName)
            : imageOption.colorPalette ?? defaultImageSettings.colorPalette,
          returnsSettings: availableReturns,
        };
      } else {
        console.warn('Skipping base option due to missing coloringMode:', {
          baseReturnSettings,
          ...imageOption,
        });
      }
      return acc;
    },
    {} as Record<COLOR_MODE | string, ImageSettings>
  );
};

export const defaultState: State = {
  hasVizError: false,
  vizErrorMessage: '',
  metadata: undefined, // Will become an array
  pointCloudSensorsSettings: defaultPointCloudSensorsSettings,
  pointCloudGlobalSettings: defaultPointCloudGlobalSettings,

  dataMessage: new Subject<DataMessage[]>(),
  pointCloudObservable: NEVER,

  availableChannels: [],
  rgbChannels: [],

  // need to instantiate after data profile loads, doing reflectivity for now
  pcScaler: new PointcloudScaler(COLOR_MODE.REFLECTIVITY, false),
  pcAutoLeveler: new PointcloudAutoLeveler(COLOR_MODE.REFLECTIVITY, false),
  pcNoiseSuppressor: new PointcloudNoiseSuppressor(
    COLOR_MODE.REFLECTIVITY,
    false
  ),
  pcColorers: {},

  modeSpecificPalettes: {},

  imageObservable: NEVER,
  twoDImageSettings: defaultTwoDImageSettings,
  imageDestaggerer: new ImageDestaggerer([]),
  imageScaler: new ImageScaler([]),
  imageSuppressor: new ImageNoiseSuppressor([]),
  imageAutoLeveler: new ImageAutoleveler([]),
  imageColorers: [],

  cameraPreset: CAMERA_PRESETS[0],
  isCameraDirty: false,
  pointShape: PointShape.square,
  pointSize: DEFAULT_POINT_SIZE,
  autoLevelImage: true,
  autoLevelPointCloud: true,
  nearIRNoiseSuppressed: true,
  availablePointColorModes: Object.keys(allColorPalettes),
  availableImageColorModes: [],
  colorPalettes: [],
  is2DImageVisible: true,
  availableImageReturnsCombination: SINGLE_RETURN_IMAGE_COMBINATIONS,
  imageReturnsCombination: ImageReturnsCombination.return1Only,
  areRingsVisible: true,

  leftSidebarExpanded: true,
  flipped: false,
};

/**
 * TODOS:
 * Eliminate point shape toggling
 */
export interface VizSceneStateModel extends State {
  setHasVizError: EzPzAction<VizSceneStateModel, boolean>;
  setVizErrorMessage: EzPzAction<VizSceneStateModel, string>;
  // Unification - state for left sidebar component
  _setMetadata: EzPzAction<VizSceneStateModel, any>; // Payload is SensorMetadata[]
  updateMetadata: EzPzThunk<VizSceneStateModel, Sensor[]>; // Payload is SensorMetadata[]
  _setPcColorers: EzPzAction<
    VizSceneStateModel,
    Record<string, PointcloudColorer>
  >;
  recreateObservables: EzPzThunk<VizSceneStateModel>;
  _setPointCloudGlobalSettings: EzPzAction<
    VizSceneStateModel,
    Partial<PointCloudGlobalSettings>
  >;
  updatePointCloudGlobalSettings: EzPzThunk<
    VizSceneStateModel,
    Partial<PointCloudGlobalSettings>
  >;

  _setPointCloudSensorsSettings: EzPzAction<
    VizSceneStateModel,
    PointCloudSensorsSettings
  >;
  // Payload is [sensorSerialNumber, partialSettingsForThatSensor]
  updatePointCloudSensorsSettings: EzPzThunk<
    VizSceneStateModel,
    [string, Partial<PointCloudSensorsSettings[string]>]
  >;

  _setTwoDImageSettings: EzPzAction<
    VizSceneStateModel,
    Partial<TwoDImageState>
  >;

  updateTwoDImageSettings: EzPzThunk<
    VizSceneStateModel,
    Partial<TwoDImageState>
  >;
  swapImages: EzPzThunk<
    VizSceneStateModel,
    { sensorSn: string; indexA: number; indexB: number }
  >;
  addImage: EzPzThunk<VizSceneStateModel, string>;
  deleteImage: EzPzThunk<
    VizSceneStateModel,
    { sensorSn: string; index: number }
  >;
  updateImage: EzPzThunk<
    VizSceneStateModel,
    { sensorSn: string; index: number; partialSettings: Partial<ImageSettings> }
  >;
  togglePointCloudVisibility: EzPzThunk<VizSceneStateModel>;

  _setSelectedImagePanelSn: EzPzAction<VizSceneStateModel, string | null>;
  setSelectedImagePanelSn: EzPzThunk<VizSceneStateModel, string | null>;
  currentSensorImageSettings: Computed<VizSceneStateModel, ImageSettings[]>;

  // Data message subject and observables
  _setDataMessage: EzPzAction<VizSceneStateModel, Subject<DataMessage[]>>;
  _setPointCloudObservable: EzPzAction<
    VizSceneStateModel,
    Observable<RangeRGBData[][]>
  >;
  _setImageObservable: EzPzAction<VizSceneStateModel, Observable<DataMessage>>;
  updateDataMessage: EzPzThunk<VizSceneStateModel, Subject<DataMessage[]>>; // Create pausable and observables

  // Cameras
  cycleCameraPresets: EzPzThunk<VizSceneStateModel>;
  setCameraPresetByName: EzPzThunk<VizSceneStateModel, string>;
  _setCameraPreset: EzPzAction<VizSceneStateModel, CameraPreset>;
  toggleCameraOrthographicMode: EzPzThunk<VizSceneStateModel>;
  setIsCameraOrthographic: EzPzAction<VizSceneStateModel, boolean>;
  setIsCameraDirty: EzPzAction<VizSceneStateModel, boolean>;

  // Point Appearance
  cyclePointShape: EzPzThunk<VizSceneStateModel>;
  _setPointShape: EzPzAction<VizSceneStateModel, PointShape>;
  incrementPointSize: EzPzThunk<VizSceneStateModel>;
  decrementPointSize: EzPzThunk<VizSceneStateModel>;
  setPointSize: EzPzAction<VizSceneStateModel, number>;

  // Color Modes
  setAvailablePointColorModes: EzPzThunk<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  setAvailableImageColorModes: EzPzThunk<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  _setAvailablePointColorModes: EzPzAction<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  _setAvailableImageColorModes: EzPzAction<
    VizSceneStateModel,
    (COLOR_MODE | string)[]
  >;
  setDefaultPointColorMode: EzPzThunk<VizSceneStateModel>;
  setDefaultImageColorMode: EzPzThunk<VizSceneStateModel>;
  cyclePointColorMode: EzPzThunk<VizSceneStateModel>;
  cycleImageColorMode: EzPzThunk<VizSceneStateModel>;
  setPointColorMode: EzPzThunk<VizSceneStateModel, COLOR_MODE | string>;
  setImageColorMode: EzPzThunk<VizSceneStateModel, COLOR_MODE | string>;
  _setPointColorMode: EzPzAction<VizSceneStateModel, COLOR_MODE | string>;
  _setImageColorMode: EzPzAction<VizSceneStateModel, COLOR_MODE | string>;
  _handlePointCalRefPaletteSwitch: EzPzThunk<
    VizSceneStateModel,
    COLOR_MODE | string
  >;
  _handleImageCalRefPaletteSwitch: EzPzThunk<
    VizSceneStateModel,
    COLOR_MODE | string
  >;
  toggleImageAutoLevel: EzPzThunk<VizSceneStateModel>;
  togglePointCloudAutoLevel: EzPzThunk<VizSceneStateModel>;
  _setImageAutoLevel: EzPzAction<VizSceneStateModel, boolean>;
  _setPointCloudAutoLevel: EzPzAction<VizSceneStateModel, boolean>;
  toggleNearIRNoiseSuppression: EzPzThunk<VizSceneStateModel>;
  _setNearIRNoiseSuppressed: EzPzAction<VizSceneStateModel, boolean>;

  // Color Palettes
  setColorPalettes: EzPzThunk<VizSceneStateModel, string[]>;
  _setColorPalettes: EzPzAction<VizSceneStateModel, string[]>;
  setDefaultPointColorPalette: EzPzThunk<VizSceneStateModel>;
  setDefaultImageColorPalette: EzPzThunk<VizSceneStateModel>;
  cyclePointColorPalette: EzPzThunk<VizSceneStateModel>;
  setPointColorPalette: EzPzAction<VizSceneStateModel, string>;
  setImageColorPalette: EzPzAction<VizSceneStateModel, string>;
  _setPreviousPointColorPalette: EzPzAction<
    VizSceneStateModel,
    string | undefined
  >;
  _setPreviousImageColorPalette: EzPzAction<
    VizSceneStateModel,
    string | undefined
  >;
  setHasCalibratedReflectivity: EzPzThunk<VizSceneStateModel, boolean>;
  _setHasCalibratedReflectivity: EzPzAction<VizSceneStateModel, boolean>;

  // 2D Image State
  toggle2DImageVisibility: EzPzThunk<VizSceneStateModel>;
  setIs2DImageVisible: EzPzAction<VizSceneStateModel, boolean>;

  // Range Rings
  toggleRangeRingsVisibility: EzPzThunk<VizSceneStateModel>;
  setAreRangeRingsVisible: EzPzAction<VizSceneStateModel, boolean>;

  // Angle Rulers
  toggleAngleRulersVisibility: EzPzThunk<VizSceneStateModel>;
  setAreAngleRulersVisible: EzPzAction<VizSceneStateModel, boolean>;

  setLeftSidebarExpanded: EzPzAction<VizSceneStateModel, boolean>;
  setFlipped: EzPzAction<VizSceneStateModel, boolean>;

  // Init available channels
  initAvailableChannels: EzPzThunk<VizSceneStateModel>;
  setAvailableChannels: EzPzAction<
    VizSceneStateModel,
    (CHANNEL_FIELD | string)[]
  >;

  // RGB channels
  setRgbChannels: EzPzAction<VizSceneStateModel, string[]>;
}

// TODO (dirty state for autolv/NS)
export const model: VizSceneStateModel = {
  ...defaultState,
  setHasVizError: action((state, hasError) => {
    state.hasVizError = hasError;
  }),
  setVizErrorMessage: action((state, errorMessage) => {
    state.vizErrorMessage = errorMessage;
  }),
  _setMetadata: action((state, metadata) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    state.metadata = metadata; // metadata is now SensorMetadata[]
  }),
  _setPcColorers: action((state, pcColorers) => {
    state.pcColorers = pcColorers;
  }),

  _setSelectedImagePanelSn: action((state, sn) => {
    state.twoDImageSettings.selectedImagePanelSn = sn;
  }),

  setAvailableChannels: action((state, channels) => {
    state.availableChannels = sortChannels(channels);
  }),

  setRgbChannels: action((state, channels) => {
    state.rgbChannels = channels.sort();
  }),

  currentSensorImageSettings: computed(
    // State dependencies for memoization
    [
      (state) => state.twoDImageSettings.imageSettings,
      (state) => state.twoDImageSettings.selectedImagePanelSn,
    ],
    (imageSettings, selectedImagePanelSn): ImageSettings[] => {
      if (
        selectedImagePanelSn &&
        imageSettings &&
        imageSettings[selectedImagePanelSn]
      ) {
        return imageSettings[selectedImagePanelSn];
      }
      return []; // Return empty array if no sensor selected or settings don't exist
    }
  ),

  updateMetadata: thunk((actions, sensorsMetadata: any[], { getState }) => {
    if (!sensorsMetadata || sensorsMetadata.length === 0) {
      return;
    }

    const { coloringMode, colorPalette } = getState().pointCloudGlobalSettings;
    const { rgbChannels } = getState();

    // Create colorers for each sensor
    const newPcColorers: Record<string, PointcloudColorer> = {};
    const isRgbMode =
      rgbChannels.length > 0 && rgbChannels.includes(coloringMode);
    sensorsMetadata.forEach((metadata) => {
      const sn = metadata?.productSn ?? 'unknown';
      newPcColorers[sn] = new PointcloudColorer(
        coloringMode,
        allColorPalettes[colorPalette],
        isRgbMode
      );
    });
    actions._setPcColorers(newPcColorers);

    const firstSensorMetadata = sensorsMetadata[0];
    const firstSensorUdpProfileLidar =
      extractLidarProfileFromMetadata(firstSensorMetadata);

    const allPcSensorsSettings: PointCloudSensorsSettings = {};
    const sensorSerialNumbers: string[] = [];
    const initialImageSettings: Record<string, ImageSettings[]> = {};

    sensorsMetadata.forEach((metadata) => {
      const sn = metadata?.productSn ?? 'unknown';
      sensorSerialNumbers.push(sn); // Collect serial numbers

      // Get settings specific to this sensor
      const sensorSettings = getPointCloudSensorsSettings(
        coloringMode, // Use global coloring mode for initial settings
        sn,
        getState().availableChannels
      );

      if (sensorSettings && sensorSettings[sn]) {
        allPcSensorsSettings[sn] = sensorSettings[sn];
      } else {
        console.warn(`Could not get point cloud settings for sensor SN: ${sn}`);
      }

      initialImageSettings[sn] = [
        JSON.parse(JSON.stringify(defaultImageSettings)),
      ];
    });

    const initialSelectedSn =
      sensorSerialNumbers.length > 0 ? sensorSerialNumbers[0] : null;
    actions.setSelectedImagePanelSn(initialSelectedSn);

    // Update the state with the settings for all sensors
    actions._setPointCloudSensorsSettings(allPcSensorsSettings);
    actions._setMetadata(sensorsMetadata); // Store full metadata array

    cachedSensorReturnNumbers = {};
    cachedColorerReturns = {};
    sensorSerialNumbers.forEach((sn) => {
      const sensorSettings = allPcSensorsSettings[sn]?.returnsSettings;
      const returnNumbers: number[] = [];
      const colorerReturns: ReturnColor[] = [];
      if (sensorSettings?.length > 0) {
        sensorSettings.forEach((setting, index) => {
          if (setting.visible) {
            returnNumbers.push(index);
            colorerReturns.push({
              index,
              highlightColor: setting.highlightColor ?? null,
            });
          }
        });
      }
      cachedSensorReturnNumbers[sn] = returnNumbers;
      cachedColorerReturns[sn] = colorerReturns;
    });
    // --- Global Settings (Color Modes, etc.) ---
    const availableColorModes = allColorModes.filter((channelKey) =>
      Object.prototype.hasOwnProperty.call(
        PROFILES[firstSensorUdpProfileLidar], // Based on first sensor for now
        channelKey
      )
    );
    actions.setAvailablePointColorModes(availableColorModes);
    actions.setAvailableImageColorModes(availableColorModes);

    if (!availableColorModes.includes(COLOR_MODE.SIGNAL)) {
      // Iterate through the initialized image settings for each sensor
      Object.keys(initialImageSettings).forEach((sn) => {
        const sensorImageSettings = initialImageSettings[sn];
        // Check if the first (default) image setting for this sensor is SIGNAL
        if (
          sensorImageSettings.length === 1 &&
          sensorImageSettings[0].coloringMode === COLOR_MODE.SIGNAL
        ) {
          sensorImageSettings[0].coloringMode = COLOR_MODE.REFLECTIVITY;
        }
      });
    }

    // Initialize 2D Image Settings for ALL sensors
    const imageInfoMap: TwoDImageState['imageInfo'] = {};
    sensorsMetadata.forEach((meta) => {
      const sn = meta?.productSn ?? 'unknown';
      const df = meta?.dataFormat;
      if (
        sn &&
        df?.columnsPerFrame &&
        df?.pixelsPerColumn &&
        meta?.beamAltitudeDegrees
      ) {
        imageInfoMap[sn] = {
          dimensions: [df.columnsPerFrame, df.pixelsPerColumn],
          aspectRatio: imageAspectRatio(meta.beamAltitudeDegrees),
        };
      }
    });
    actions.updateTwoDImageSettings({
      imageSettings: initialImageSettings, // Set the initialized settings record
      imageInfo: imageInfoMap,
      selectedImagePanelSn: initialSelectedSn, // Ensure selected SN is part of the update
    });
  }),

  _setPointCloudGlobalSettings: action((state, newSettings) => {
    const oldPointCloudGlobalSettings = { ...state.pointCloudGlobalSettings };
    const { rgbChannels } = state;
    if (Object.keys(newSettings).length > 1)
      throw new Error('Trying to set too many point cloud settings at once');
    const currentMode = oldPointCloudGlobalSettings.coloringMode;
    const currentPalette = oldPointCloudGlobalSettings.colorPalette;
    if (newSettings.coloringMode) {
      const { coloringMode } = newSettings;

      oldPointCloudGlobalSettings.coloringMode = coloringMode;

      Object.values(state.pcColorers).forEach((colorer) => {
        colorer.channelField = coloringMode;
        if (rgbChannels && rgbChannels.includes(coloringMode)) {
          colorer.isRgb = true;
        } else {
          colorer.isRgb = false;
        }
      });
      state.pcAutoLeveler.channelField = coloringMode;

      if (rgbChannels && rgbChannels.includes(coloringMode)) {
        // Set palette to RGB and disable level/scaling/noise transforms
        oldPointCloudGlobalSettings.isAutoLevelsDisabled = true;
        oldPointCloudGlobalSettings.isAutoLevelsOn = false;
        oldPointCloudGlobalSettings.isNoiseSuppressionDisabled = true;
        oldPointCloudGlobalSettings.isNoiseSuppressionOn = false;
        state.pcAutoLeveler.isActive = false;
        state.pcScaler.isActive = false;
        state.pcNoiseSuppressor.isActive = false;
        // Update all colorers to use RGB palette if it exists, else keep current
        Object.values(state.pcColorers).forEach((colorer) => {
          colorer.colorMap =
            allColorPalettes['RGB' as ColorPaletteName] ?? colorer.colorMap;
        });
        // Ensure RGB palette is listed in options
        if (
          !oldPointCloudGlobalSettings.colorPaletteOptions.includes(
            'RGB' as ColorPaletteName
          )
        ) {
          oldPointCloudGlobalSettings.colorPaletteOptions = [
            ...oldPointCloudGlobalSettings.colorPaletteOptions,
            'RGB' as ColorPaletteName,
          ];
        }
        oldPointCloudGlobalSettings.colorPalette = 'RGB' as ColorPaletteName;
      } else {
        // Remove RGB palette option if present
        oldPointCloudGlobalSettings.colorPaletteOptions =
          oldPointCloudGlobalSettings.colorPaletteOptions.filter(
            (palette) => palette !== ('RGB' as ColorPaletteName)
          );
        if (
          !coloringMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          !(rgbChannels.length > 0 && rgbChannels.includes(coloringMode))
        ) {
          oldPointCloudGlobalSettings.isAutoLevelsDisabled = false;
          oldPointCloudGlobalSettings.isAutoLevelsOn = true;
          state.pcAutoLeveler.isActive = true;
          state.pcScaler.isActive = false;

          const paletteToRestore = isColorMode(coloringMode)
            ? state.modeSpecificPalettes[coloringMode as COLOR_MODE] ??
              ('Ouster' as ColorPaletteName)
            : ('Ouster' as ColorPaletteName);
          oldPointCloudGlobalSettings.colorPalette = paletteToRestore;
          Object.values(state.pcColorers).forEach((colorer) => {
            colorer.colorMap =
              allColorPalettes[oldPointCloudGlobalSettings.colorPalette];
          });
          state.pcScaler.channelField = coloringMode;
        } else {
          if (
            !currentMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
            currentPalette
          ) {
            // Save the palette of the mode we are leaving
            if (isColorMode(currentMode)) {
              state.modeSpecificPalettes[currentMode as COLOR_MODE] =
                currentPalette;
            }
          }

          oldPointCloudGlobalSettings.isAutoLevelsDisabled = true;
          oldPointCloudGlobalSettings.isAutoLevelsOn = false;
          oldPointCloudGlobalSettings.colorPalette =
            'CalRef' as ColorPaletteName;
          Object.values(state.pcColorers).forEach((colorer) => {
            colorer.colorMap = allColorPalettes['CalRef' as ColorPaletteName];
          });
          state.pcScaler.isActive = true;
          state.pcAutoLeveler.isActive = false;
        }
      }

      if (coloringMode === COLOR_MODE.NEAR_IR) {
        oldPointCloudGlobalSettings.isNoiseSuppressionDisabled = false;
        oldPointCloudGlobalSettings.isNoiseSuppressionOn = true;
        state.pcNoiseSuppressor.channelField = coloringMode;
        state.pcNoiseSuppressor.isActive = true;
      } else {
        oldPointCloudGlobalSettings.isNoiseSuppressionDisabled = true;
        oldPointCloudGlobalSettings.isNoiseSuppressionOn = false;
        state.pcNoiseSuppressor.isActive = false;
      }
      // update sensor settings
    } else if (newSettings.colorPalette) {
      if (
        (oldPointCloudGlobalSettings.coloringMode?.startsWith(
          COLOR_MODE.REFLECTIVITY
        ) &&
          newSettings.colorPalette === ('CalRef' as ColorPaletteName)) ||
        (rgbChannels.length > 0 &&
          rgbChannels.includes(oldPointCloudGlobalSettings.coloringMode))
      ) {
        state.pcAutoLeveler.isActive = false;
        oldPointCloudGlobalSettings.isAutoLevelsDisabled = true;
        oldPointCloudGlobalSettings.isAutoLevelsOn = false;
      } else {
        oldPointCloudGlobalSettings.isAutoLevelsDisabled = false;
        oldPointCloudGlobalSettings.isAutoLevelsOn = true;
        state.pcAutoLeveler.isActive = true;
      }

      const newPalette = newSettings.colorPalette;
      oldPointCloudGlobalSettings.colorPalette = newPalette;
      if (
        !currentMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
        newPalette !== 'RGB'
      ) {
        if (isColorMode(currentMode)) {
          state.modeSpecificPalettes[currentMode as COLOR_MODE] =
            newPalette as ColorPaletteName;
        }
        Object.values(state.pcColorers).forEach((colorer) => {
          colorer.colorMap = allColorPalettes[newPalette];
        });
      }
    } else if (!isUndefined(newSettings.isAutoLevelsOn)) {
      oldPointCloudGlobalSettings.isAutoLevelsOn = newSettings.isAutoLevelsOn;
      state.pcAutoLeveler.isActive = newSettings.isAutoLevelsOn;
      state.pcScaler.isActive = !newSettings.isAutoLevelsOn;
    } else if (!isUndefined(newSettings.isNoiseSuppressionOn)) {
      oldPointCloudGlobalSettings.isNoiseSuppressionOn =
        newSettings.isNoiseSuppressionOn;
      state.pcNoiseSuppressor.isActive = newSettings.isNoiseSuppressionOn;
    } else if (!isUndefined(newSettings.showPointCloud)) {
      oldPointCloudGlobalSettings.showPointCloud = newSettings.showPointCloud;
    } else if (newSettings.pointSize) {
      oldPointCloudGlobalSettings.pointSize = newSettings.pointSize;
    } else if (!isUndefined(newSettings.showRangeRings)) {
      oldPointCloudGlobalSettings.showRangeRings = newSettings.showRangeRings;
    } else if (newSettings.colorModeOptions) {
      const validColorModes = newSettings.colorModeOptions;
      oldPointCloudGlobalSettings.colorModeOptions = validColorModes;
    } else if (newSettings.colorPaletteOptions) {
      oldPointCloudGlobalSettings.colorPaletteOptions =
        newSettings.colorPaletteOptions;
    } else if (!isUndefined(newSettings.isOrthographicCamera)) {
      oldPointCloudGlobalSettings.isOrthographicCamera =
        newSettings.isOrthographicCamera;
    }

    state.pointCloudGlobalSettings = oldPointCloudGlobalSettings;
  }),

  updatePointCloudGlobalSettings: thunk(
    (actions, newSettings, { getState }) => {
      // Set PointCloudGlobalSettings object, we don't use this in the DA directly atm
      actions._setPointCloudGlobalSettings(newSettings);
      // Only update a single setting at a time
      if (Object.keys(newSettings).length > 1)
        throw new Error('Trying to set too many point cloud settings at once');

      // Track if we need to recreate observables
      let needsObservableRecreation = false;

      if (newSettings.coloringMode) {
        needsObservableRecreation = true;
        actions.setPointColorMode(newSettings.coloringMode); // use existing data app logic

        const { metadata, availableChannels } = getState(); // metadata is SensorMetadata[]
        if (!Array.isArray(metadata) || metadata.length === 0) return;

        const allPcSensorsSettings: PointCloudSensorsSettings = {};
        metadata.forEach((sensorMeta) => {
          const sn = sensorMeta?.productSn ?? 'unknown';
          const sensorSettings = getPointCloudSensorsSettings(
            newSettings.coloringMode, // Use the NEW coloring mode
            sn,
            availableChannels
          );
          if (sensorSettings && sensorSettings[sn]) {
            allPcSensorsSettings[sn] = sensorSettings[sn];
            // Reset cached colorer's highlight colors to null when coloring mode changes
            const { returnsSettings } = sensorSettings[sn];
            cachedColorerReturns[sn] = (returnsSettings ?? []).map(
              (setting, index) => ({
                index,
                highlightColor: null,
              })
            );
          }
        });
        actions._setPointCloudSensorsSettings(allPcSensorsSettings);
      } else if (newSettings.colorPalette) {
        needsObservableRecreation = true;
        actions.setPointColorPalette(newSettings.colorPalette);
        // Reset cached colorer's highlight colors to null when coloring palette changes
        const sensorsSettings = { ...getState().pointCloudSensorsSettings };
        Object.keys(sensorsSettings).forEach((sn) => {
          if (sensorsSettings[sn].returnsSettings) {
            sensorsSettings[sn].returnsSettings = sensorsSettings[
              sn
            ].returnsSettings.map((setting) => ({
              ...setting,
              highlightColor: null,
            }));
          }
          cachedColorerReturns[sn] = (
            sensorsSettings[sn].returnsSettings ?? []
          ).map((setting, index) => ({ index, highlightColor: null }));
        });
        const currentState = getState();
        Object.values(currentState.pcColorers).forEach((colorer) => {
          colorer.colorMap =
            allColorPalettes[
              newSettings.colorPalette as keyof typeof allColorPalettes
            ];
        });
      } else if (!isUndefined(newSettings.isAutoLevelsOn)) {
        needsObservableRecreation = true;
        actions._setPointCloudAutoLevel(newSettings.isAutoLevelsOn);
      } else if (!isUndefined(newSettings.isNoiseSuppressionOn)) {
        needsObservableRecreation = true;
        actions._setNearIRNoiseSuppressed(newSettings.isNoiseSuppressionOn);
      } else if (newSettings.pointSize) {
        needsObservableRecreation = true;
        actions.setPointSize(newSettings.pointSize);
      } else if (!isUndefined(newSettings.showRangeRings)) {
        actions.setAreRangeRingsVisible(newSettings.showRangeRings);
      } else if (newSettings.colorModeOptions) {
        actions.setAvailablePointColorModes(newSettings.colorModeOptions);
      } else if (newSettings.colorPaletteOptions) {
        actions.setColorPalettes(newSettings.colorPaletteOptions);
      } else if (!isUndefined(newSettings.isOrthographicCamera)) {
        // ORTHO ACTION
        actions.setIsCameraOrthographic(newSettings.isOrthographicCamera); // Added action call
      }

      // Recreate observables if operators were updated
      if (needsObservableRecreation) {
        actions.recreateObservables();
      }
    }
  ),

  togglePointCloudVisibility: thunk((actions, _, { getState }) => {
    const vis = getState().pointCloudGlobalSettings.showPointCloud;
    actions.updatePointCloudGlobalSettings({ showPointCloud: !vis });
    actions.recreateObservables();
    return !vis;
  }),

  _setPointCloudSensorsSettings: action((state, settings) => {
    state.pointCloudSensorsSettings = settings;
  }),

  updatePointCloudSensorsSettings: thunk(
    (actions, [sn, newSettings], { getState }) => {
      const oldSensorsSettings = getState().pointCloudSensorsSettings;
      // Create a new object for the updated settings
      const newSensorsSettings = {
        ...oldSensorsSettings,
        [sn]: {
          ...(oldSensorsSettings[sn] ?? {}),
          ...newSettings,
        },
      };
      actions._setPointCloudSensorsSettings(newSensorsSettings);

      // --- Update Cache for the modified sensor ---
      const updatedSensorSettings = newSensorsSettings[sn]?.returnsSettings;
      const newReturnNumbers: number[] = [];
      const newColorerReturns: ReturnColor[] = [];
      let hasReturnsChange = false;
      if (updatedSensorSettings?.length > 0) {
        hasReturnsChange = true;
        updatedSensorSettings.forEach((setting, index) => {
          if (setting.visible) {
            newReturnNumbers.push(index);
            newColorerReturns.push({
              index,
              highlightColor: setting.highlightColor ?? null,
            });
          }
        });
      }
      cachedSensorReturnNumbers[sn] = newReturnNumbers;
      cachedColorerReturns[sn] = newColorerReturns;

      // Recreate observables if returns settings or visibility changed
      if (hasReturnsChange || newSettings.sensorVisibility !== undefined) {
        actions.recreateObservables();
      }
    }
  ),

  _setDataMessage: action((state, dataMessage) => {
    state.dataMessage = dataMessage;
  }),
  _setPointCloudObservable: action((state, obs) => {
    state.pointCloudObservable = obs;
  }),
  _setImageObservable: action((state, obs) => {
    state.imageObservable = obs;
  }),

  // Recreate observables using the existing dataMessage subject
  // This is needed when operators change but we want to keep the same subject
  recreateObservables: thunk((actions, _, { getState }) => {
    const state = getState();
    const currentDataMessage = state.dataMessage as Subject<DataMessage[]>;

    if (currentDataMessage) {
      // Recreate the observables with updated operators
      actions.updateDataMessage(currentDataMessage);

      const lastData = frameCache.lastDataMessage;

      if (lastData && lastData.length > 0) {
        setTimeout(() => {
          currentDataMessage.next(lastData);
        }, 0);
      }
    }
  }),

  updateDataMessage: thunk((actions, datamessage, { getState }) => {
    const { _setDataMessage, _setPointCloudObservable, _setImageObservable } =
      actions;
    _setDataMessage(datamessage); // Store the raw subject

    // Get operators - they are configured elsewhere now (updateMetadata, updatePointCloudSensorsSettings)
    const {
      imageAutoLeveler,
      imageDestaggerer,
      imageSuppressor,
      imageScaler,
      pcNoiseSuppressor,
      pcScaler,
      pcAutoLeveler,
      pointCloudGlobalSettings,
      rgbChannels,
    } = getState();

    const getSensorColorerForMessage = (
      sensorSn: string
    ): PointcloudColorer => {
      const currentState = getState();
      if (!currentState.pcColorers[sensorSn]) {
        const isRgbMode =
          rgbChannels.length > 0 &&
          rgbChannels.includes(pointCloudGlobalSettings.coloringMode);
        // Create new colorer for this sensor
        const newColorer = new PointcloudColorer(
          pointCloudGlobalSettings.coloringMode,
          allColorPalettes[pointCloudGlobalSettings.colorPalette],
          isRgbMode
        );

        // Update the state properly using the action
        const updatedColorers = {
          ...currentState.pcColorers,
          [sensorSn]: newColorer,
        };
        actions._setPcColorers(updatedColorers);

        return newColorer;
      }
      return currentState.pcColorers[sensorSn];
    };

    // --- Define transformPoints ---
    const transformPoints = (msg: PointsDataMessage): RangeRGBData[] => {
      try {
        const sensorSn = msg?.sensorInfo?.sn;
        if (!sensorSn) {
          // console.warn('Message missing sensor SN in transformPoints', msg);
          return [];
        }

        const sensorSpecificReturnNumbers =
          cachedSensorReturnNumbers[sensorSn] ?? [];
        const colorerWhichReturns = cachedColorerReturns[sensorSn] ?? [];

        pcNoiseSuppressor.whichReturns = sensorSpecificReturnNumbers;
        pcScaler.whichReturns = sensorSpecificReturnNumbers;
        pcAutoLeveler.whichReturns = sensorSpecificReturnNumbers;

        // Disable scaler for RGB mode
        const currentColorMode = pointCloudGlobalSettings.coloringMode;
        const shouldScale =
          rgbChannels.length > 0 &&
          !rgbChannels.includes(currentColorMode) &&
          !currentColorMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
          pcScaler.isActive;
        pcScaler.isActive = shouldScale;

        const suppressed = pcNoiseSuppressor.suppressPointcloudNoise(msg);
        const scaled = pcScaler.scalePointcloud(suppressed);
        const autoLeveled = pcAutoLeveler.autoLevelPointcloud(scaled);
        if (autoLeveled === null) return [];

        const sensorColorer = getSensorColorerForMessage(sensorSn);
        sensorColorer.whichReturns = colorerWhichReturns;

        // Use the sensor-specific colorer
        const colored = sensorColorer.colorPointcloud(autoLeveled);

        return colored.filter(
          (point) => point.rangeArray && point.rangeArray.length > 0
        );
      } catch (e) {
        console.error(
          `Error in transformPoints for sensor ${msg?.sensorInfo?.sn}:`,
          e
        );
        return [];
      }
    };

    const processPointCloudBatch = (msgs: DataMessage[]) =>
      from(msgs).pipe(
        filter((msg) => {
          const sensorSettings = getState().pointCloudSensorsSettings;
          const sn = msg?.sensorInfo?.sn;
          const setting = sn ? sensorSettings[sn] : undefined;
          // If sensor setting exists and sensorVisibility is false, filter out.
          return setting ? setting.sensorVisibility !== false : true;
        }),
        mergeMap((msg: DataMessage) =>
          from([copyDataMessage(msg)]).pipe(
            operators.convertDataMessageForPointcloud(),
            map(transformPoints), // Use the updated transformPoints
            catchError((err) => {
              console.error('!!! Error in processPointCloud pipeline:', err);
              return of([]);
            })
          )
        ),
        toArray() // Collect all processed results into an array
      );

    const pcObservable = datamessage.pipe(
      mergeMap((msgs: DataMessage[]) => processPointCloudBatch(msgs)),
      shareReplay(1)
    );

    /*
      In below, we optimize the image processing by chaining the image operators
      together and only processing the image once.
      Also, we clean up the pipeline, so it doesn't share twice and reconnect every time.
      To make sure the image is updated even on pause, we have the useEffect on DriveViewer, so when the image setting change, the message will be resent and the pipeline will be reconnected.
    */
    const processImage = (msg: DataMessage) =>
      imageScaler.mapImage(
        imageAutoLeveler.mapImage(
          imageSuppressor.mapImage(
            imageDestaggerer.mapImage(copyDataMessage(msg))
          )
        )
      );

    const imgObservable = datamessage.pipe(
      map((msgs: DataMessage[]) => {
        // Assuming selected sensor SN is stored in twoDImageSettings.selectedImagePanelSn
        const selectedSn = getState().twoDImageSettings.selectedImagePanelSn;
        return msgs.find((msg) => msg?.sensorInfo?.sn === selectedSn);
      }),
      filter((msg): msg is DataMessage => Boolean(msg)),
      observeOn(animationFrameScheduler), // schedule work asynchronously
      map(processImage),
      share()
    );

    _setPointCloudObservable(pcObservable);
    _setImageObservable(imgObservable);
  }),

  cycleCameraPresets: thunk((actions, _, { getState }) => {
    const { cameraPreset } = getState();

    const currentPresetName = cameraPreset.name;
    const currentPresetIndex = CAMERA_PRESETS.findIndex(
      (camPreset) => camPreset.name === currentPresetName
    );

    if (currentPresetIndex === -1) {
      throw new Error(
        `Current camera preset ${currentPresetName} is not valid. Cannot cycle.`
      );
    }

    const newPreset =
      CAMERA_PRESETS[mod(currentPresetIndex + 1, CAMERA_PRESETS.length)];

    actions._setCameraPreset(newPreset);

    return getState().cameraPreset;
  }),

  setCameraPresetByName: thunk((actions, cameraPresetName) => {
    const cameraPreset = CAMERA_PRESETS.find(
      (camPreset) => camPreset.name === cameraPresetName
    );

    if (!cameraPreset) {
      throw new Error(
        `Can't find "${cameraPresetName}" view in available presets`
      );
    }

    // Clone the pose and preset so that it always trigger a reset of
    // the camera in the WebvizView component. We need this because we
    // do not know whether the user has move the camera around since the
    // last time they selected this preset
    const cameraPresetClone = { ...cameraPreset };
    cameraPresetClone.pose = { ...cameraPreset.pose };

    actions._setCameraPreset(cameraPresetClone);
  }),

  _setCameraPreset: action((state, camPreset) => {
    state.cameraPreset = camPreset;

    // Whenever the user sets a camera preset directly, the camera view
    // matches the preset and we consider it 'clean'. If later the user
    // rotates/pans the view with their mouse, components can call
    // setIsCameraDirty(true) to set it back to 'dirty'
    state.isCameraDirty = false;
  }),

  toggleCameraOrthographicMode: thunk((actions, _, { getState }) => {
    const wasCameraOrthographic =
      getState().pointCloudGlobalSettings.isOrthographicCamera;

    actions.updatePointCloudGlobalSettings({
      isOrthographicCamera: !wasCameraOrthographic,
    });

    return !wasCameraOrthographic;
  }),

  setIsCameraOrthographic: action((state, isOrthographic) => {
    state.pointCloudGlobalSettings.isOrthographicCamera = isOrthographic;
  }),

  setIsCameraDirty: action((state, isDirty) => {
    state.isCameraDirty = isDirty;
  }),

  cyclePointShape: thunk((actions, _, { getState }) => {
    const { pointShape } = getState();

    const allShapes = Object.values(PointShape);
    const currentShapeIndex = allShapes.indexOf(pointShape);

    if (currentShapeIndex === -1) {
      throw new Error(
        `Current point shape ${pointShape} is not valid. Cannot cycle.`
      );
    }

    const newShape = allShapes[mod(currentShapeIndex + 1, allShapes.length)];

    actions._setPointShape(newShape);

    return getState().pointShape;
  }),

  _setPointShape: action((state, newShape) => {
    state.pointShape = newShape;
  }),

  incrementPointSize: thunk((actions, _, { getState }) => {
    const { pointSize } = getState();

    if (!pointSize) {
      console.warn('Point size not initialized yet. Cannot increment it');
      return;
    }
    const newPointSize = pointSize + POINT_SIZE_INCREMENT;

    actions.setPointSize(newPointSize);
    actions.updatePointCloudGlobalSettings({ pointSize: newPointSize });

    return getState().pointSize;
  }),

  decrementPointSize: thunk((actions, _, { getState }) => {
    const { pointSize } = getState();

    if (!pointSize) {
      console.warn('Point size not initialized yet. Cannot decrement it');
      return;
    }

    const newPointSize = pointSize - POINT_SIZE_INCREMENT;
    actions.setPointSize(newPointSize);
    actions.updatePointCloudGlobalSettings({ pointSize: newPointSize });

    return getState().pointSize;
  }),

  // TODO (mw) ensure point size max mins are shared btw left sidebar and data app min/max consts
  setPointSize: action((state, requestedPointSize) => {
    const newPointSize = Math.min(
      Math.max(requestedPointSize, MIN_POINT_SIZE),
      MAX_POINT_SIZE
    );

    if (
      requestedPointSize < MIN_POINT_SIZE ||
      requestedPointSize > MAX_POINT_SIZE
    ) {
      console.warn(
        `New requested point size was ${requestedPointSize} ` +
          `but the size has to be within [${MIN_POINT_SIZE},` +
          `${MAX_POINT_SIZE}]. Setting it to ${newPointSize}.`
      );
    }

    state.pointSize = newPointSize;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Color Modes
  /// ///////////////////////////////////////////////////////////////////////////
  setAvailablePointColorModes: thunk((actions, modes, { getState }) => {
    // Update available modes in store
    actions._setAvailablePointColorModes(modes);
    actions._setPointCloudGlobalSettings({ colorModeOptions: modes });

    // Update current pointColorMode if needed
    const currentPointColorMode = getState().pointColorMode;
    if (
      !currentPointColorMode ||
      !getState().availablePointColorModes.includes(currentPointColorMode)
    ) {
      actions.setDefaultPointColorMode();
    }
  }),

  setAvailableImageColorModes: thunk((actions, modes, { getState }) => {
    // Update available modes in store
    actions._setAvailableImageColorModes(modes);

    // Update current imageColorMode if needed
    const currentImageColorMode = getState().imageColorMode;
    if (
      !currentImageColorMode ||
      !getState().availableImageColorModes.includes(currentImageColorMode)
    ) {
      actions.setDefaultImageColorMode();
    }
  }),

  _setAvailablePointColorModes: action((state, modes) => {
    state.availablePointColorModes = modes;
  }),

  _setAvailableImageColorModes: action((state, modes) => {
    state.availableImageColorModes = modes;
  }),

  setDefaultPointColorMode: thunk((actions, _, { getState }) => {
    const availableModes = [...getState().availablePointColorModes];

    const defaultMode = extractDefaultColorSetting(
      availableModes,
      DEFAULT_POINT_COLOR_MODES,
      'No available point color modes yet'
    );

    actions.setPointColorMode(defaultMode);
  }),

  setDefaultImageColorMode: thunk((actions, _, { getState }) => {
    const availableModes = [...getState().availableImageColorModes];

    const defaultMode = extractDefaultColorSetting(
      availableModes,
      DEFAULT_IMAGE_COLOR_MODES,
      'No available image color modes yet'
    );

    actions.setImageColorMode(defaultMode);
  }),

  // TODO FIX
  cyclePointColorMode: thunk((actions, _, { getState }) => {
    const oldMode = getState().pointColorMode;
    const availableModes = [...getState().availablePointColorModes];

    const newMode = nextColorSetting(
      availableModes,
      oldMode,
      'No available point color modes yet.'
    );

    actions.setPointColorMode(newMode);
    actions._setPointCloudGlobalSettings({ coloringMode: newMode });

    return getState().pointColorMode;
  }),

  cycleImageColorMode: thunk((actions, _, { getState }) => {
    const oldMode = getState().imageColorMode;
    const availableModes = [...getState().availableImageColorModes];

    const newMode = nextColorSetting(
      availableModes,
      oldMode,
      'No available image color modes yet.'
    );

    actions.setImageColorMode(newMode);

    return getState().imageColorMode;
  }),

  setPointColorMode: thunk((actions, newPointColorMode, { getState }) => {
    actions._setPointColorMode(newPointColorMode);
    actions._handlePointCalRefPaletteSwitch(newPointColorMode);

    return getState().pointColorMode;
  }),

  setImageColorMode: thunk((actions, newImageColorMode, { getState }) => {
    // Update colorMode
    actions._setImageColorMode(newImageColorMode);

    // When switching into any Reflectivity mode, default the palette to CalRef initially
    if (
      typeof newImageColorMode === 'string' &&
      newImageColorMode.startsWith(COLOR_MODE.REFLECTIVITY)
    ) {
      const { imageColorPalette, twoDImageSettings } = getState();
      const { selectedImagePanelSn, imageSettings } = twoDImageSettings;

      // Save current palette to restore later when switching away
      if (imageColorPalette && imageColorPalette !== 'CalRef') {
        actions._setPreviousImageColorPalette(imageColorPalette);
      }
      // Set global palette
      actions.setImageColorPalette('CalRef');

      // Also propagate CalRef to the selected sensor's image panels (initial default),
      // unless user has explicitly set a palette via updateImage.
      if (
        selectedImagePanelSn &&
        imageSettings &&
        imageSettings[selectedImagePanelSn]
      ) {
        const updatedSensorImages = imageSettings[selectedImagePanelSn].map(
          (img) => {
            // If panel is already Reflectivity mode, ensure initial palette defaults to CalRef
            if (
              typeof img.coloringMode === 'string' &&
              img.coloringMode.startsWith(COLOR_MODE.REFLECTIVITY)
            ) {
              return {
                ...img,
                colorPalette: 'CalRef' as ColorPaletteName,
                isAutoLevelsDisabled: true,
                isAutoLevelsOn: false,
              };
            }
            return img;
          }
        );

        actions.updateTwoDImageSettings({
          imageSettings: {
            ...imageSettings,
            [selectedImagePanelSn]: updatedSensorImages,
          },
        });
      }
    }

    actions._handleImageCalRefPaletteSwitch(newImageColorMode);

    return getState().imageColorMode;
  }),

  _setPointColorMode: action((state, newColorMode) => {
    if (!state.availablePointColorModes.includes(newColorMode)) {
      throw new Error(`Color mode ${newColorMode} is not valid for this data`);
    }
    state.pointColorMode = newColorMode;
  }),

  _setImageColorMode: action((state, newColorMode) => {
    if (!state.availableImageColorModes.includes(newColorMode)) {
      throw new Error(`Color mode ${newColorMode} is not valid for this data`);
    }

    state.imageColorMode = newColorMode;
  }),

  _handlePointCalRefPaletteSwitch: thunk(
    (actions, newPointColorMode, { getState }) => {
      const {
        hasCalibratedReflectivity,
        pointColorMode: currentPointColorMode,
        pointColorPalette: currentPointColorPalette,
        previousPointColorPalette,
      } = getState();
      // When the data has calibrated reflectivity, switch to calref color
      // palette when coloring by reflectivity, and revert back to
      // previous palette when switching back to something else

      if (hasCalibratedReflectivity) {
        if (
          !currentPointColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          newPointColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          currentPointColorPalette
        ) {
          actions.setPointColorPalette(CALIBRATED_REFLECTIVY_PALETTE);

          actions._setPointCloudGlobalSettings({
            colorPalette: CALIBRATED_REFLECTIVY_PALETTE as ColorPaletteName,
          });
        } else if (
          currentPointColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          !newPointColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          previousPointColorPalette
        ) {
          actions.setPointColorPalette(previousPointColorPalette);

          actions._setPointCloudGlobalSettings({
            colorPalette: previousPointColorPalette as ColorPaletteName,
          });
        }
      }
    }
  ),

  _handleImageCalRefPaletteSwitch: thunk(
    (actions, newImageColorMode, { getState }) => {
      const {
        hasCalibratedReflectivity,
        imageColorMode: currentImageColorMode,
        imageColorPalette: currentImageColorPalette,
        previousImageColorPalette,
      } = getState();

      // When the data has calibrated reflectivity, switch to calref color
      // palette when coloring by reflectivity, and revert back to
      // previous palette when switching back to something else
      if (hasCalibratedReflectivity) {
        if (
          !currentImageColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          newImageColorMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
          currentImageColorPalette
        ) {
          actions._setPreviousImageColorPalette(currentImageColorPalette);
          actions.setImageColorPalette(CALIBRATED_REFLECTIVY_PALETTE);
        } else if (
          // Use 'else if'
          currentImageColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          !newImageColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
          previousImageColorPalette
        ) {
          actions.setImageColorPalette(previousImageColorPalette);
          actions._setPreviousImageColorPalette(undefined); // Clear previous after restoring
        }
      }
    }
  ),

  toggleImageAutoLevel: thunk((actions, _, { getState }) => {
    if (
      getState().imageColorMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
      getState().hasCalibratedReflectivity
    ) {
      throw new Error(
        `Auto-levels is disabled when coloring by ${getState().imageColorMode}`
      );
    }

    const wasImageAutoLevel = getState().autoLevelImage;
    actions._setImageAutoLevel(!wasImageAutoLevel);
    return getState().autoLevelImage;
  }),

  togglePointCloudAutoLevel: thunk((actions, _, { getState }) => {
    const { rgbChannels, pointColorMode } = getState();

    if (
      pointColorMode &&
      (pointColorMode.startsWith(COLOR_MODE.REFLECTIVITY) ||
        (rgbChannels.length > 0 && rgbChannels.includes(pointColorMode)))
    ) {
      throw new Error(
        `Auto-levels is disabled when coloring by ${pointColorMode}`
      );
    }

    const wasPointCloudAutoLevel =
      getState().pointCloudGlobalSettings.isAutoLevelsOn;

    actions.updatePointCloudGlobalSettings({
      isAutoLevelsOn: !wasPointCloudAutoLevel,
    });
    return getState().autoLevelPointCloud;
  }),

  _setImageAutoLevel: action((state, shouldAutoLevelImage) => {
    state.autoLevelImage = shouldAutoLevelImage;
  }),

  _setPointCloudAutoLevel: action((state, shouldAutoLevelPointCloud) => {
    state.autoLevelPointCloud = shouldAutoLevelPointCloud;
  }),

  // TODO (mw) remove this, unused
  toggleNearIRNoiseSuppression: thunk((actions, _, { getState }) => {
    if (
      !(
        getState().imageColorMode === COLOR_MODE.NEAR_IR ||
        getState().pointColorMode === COLOR_MODE.NEAR_IR
      )
    ) {
      throw new Error(
        `Noise suppression only works when visualizing near infrared data`
      );
    }

    const wasSuppressingNoise = getState().nearIRNoiseSuppressed;
    actions._setNearIRNoiseSuppressed(!wasSuppressingNoise);
    return getState().nearIRNoiseSuppressed;
  }),

  _setNearIRNoiseSuppressed: action((state, shouldSuppressNoise) => {
    state.nearIRNoiseSuppressed = shouldSuppressNoise;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Color Palettes
  /// ///////////////////////////////////////////////////////////////////////////
  setColorPalettes: thunk((actions, palettes, { getState }) => {
    // Update palettes array in store state
    actions._setColorPalettes(palettes);
    actions._setPointCloudGlobalSettings({
      colorPaletteOptions: palettes as ColorPaletteName[],
    });

    // Update current point an image palettes if needed
    const currentImageColorPalette = getState().imageColorPalette;
    const currentPointColorPalette = getState().pointColorPalette;
    if (
      !currentImageColorPalette ||
      !getState().colorPalettes.includes(currentImageColorPalette)
    ) {
      actions.setDefaultImageColorPalette();
    }
    if (
      !currentPointColorPalette ||
      !getState().colorPalettes.includes(currentPointColorPalette)
    ) {
      actions.setDefaultPointColorPalette();
    }
  }),

  _setColorPalettes: action((state, palettes) => {
    state.colorPalettes = palettes;
  }),

  setDefaultPointColorPalette: thunk((actions, _, { getState }) => {
    const palettes = [
      ...getState().pointCloudGlobalSettings.colorPaletteOptions,
    ];

    const defaultPalette = extractDefaultColorSetting(
      palettes,
      DEFAULT_POINT_COLOR_PALETTES,
      'No available color palettes yet'
    );

    actions.setPointColorPalette(defaultPalette);
    actions._setPointCloudGlobalSettings({
      colorPalette: defaultPalette as ColorPaletteName,
    });
  }),

  // Since we have multiple images, cycling doesn't make a ton of sense
  setDefaultImageColorPalette: thunk((actions, _, { getState }) => {
    const palettes = [...getState().colorPalettes];

    const defaultPalette = extractDefaultColorSetting(
      palettes,
      DEFAULT_IMAGE_COLOR_PALETTES,
      'No available color palettes yet'
    );

    actions.setImageColorPalette(defaultPalette);
    actions._setPreviousImageColorPalette(defaultPalette);
  }),

  cyclePointColorPalette: thunk((actions, _, { getState }) => {
    const oldPalette = getState().pointColorPalette;
    const availablePalettes = [...getState().colorPalettes];

    const newPalette = nextColorSetting(
      availablePalettes,
      oldPalette,
      'No available coloring palettes yet.'
    );

    actions.setPointColorPalette(newPalette);
    actions._setPointCloudGlobalSettings({
      colorPalette: newPalette as ColorPaletteName,
    });

    return getState().pointColorPalette;
  }),

  setPointColorPalette: action((state, palette) => {
    state.pointColorPalette = palette;
  }),

  setImageColorPalette: action((state, palette) => {
    state.imageColorPalette = palette;
  }),

  _setPreviousPointColorPalette: action((state, newPalette) => {
    state.previousPointColorPalette = newPalette;
  }),

  _setPreviousImageColorPalette: action((state, newPalette) => {
    state.previousImageColorPalette = newPalette;
  }),

  setHasCalibratedReflectivity: thunk((actions, hasCalref, { getState }) => {
    actions._setHasCalibratedReflectivity(hasCalref);

    // If we find out the data has calibrated reflectivity and we were
    // already coloring by reflectivity, switch to the calref color
    // palette
    if (
      getState().hasCalibratedReflectivity &&
      getState().pointColorMode?.startsWith(COLOR_MODE.REFLECTIVITY)
    ) {
      actions.setPointColorPalette(CALIBRATED_REFLECTIVY_PALETTE);
    }
    if (
      getState().hasCalibratedReflectivity &&
      getState().imageColorMode?.startsWith(COLOR_MODE.REFLECTIVITY)
    ) {
      actions.setImageColorPalette(CALIBRATED_REFLECTIVY_PALETTE);
    }
  }),

  _setHasCalibratedReflectivity: action((state, hasCalRef) => {
    state.hasCalibratedReflectivity = hasCalRef;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // 2D Image State
  /// ///////////////////////////////////////////////////////////////////////////
  toggle2DImageVisibility: thunk((actions, _, { getState }) => {
    actions.setIs2DImageVisible(!getState().is2DImageVisible);
    actions.recreateObservables();
    return getState().is2DImageVisible;
  }),

  setIs2DImageVisible: action((state, isVisible) => {
    state.is2DImageVisible = isVisible;
  }),

  /// ///////////////////////////////////////////////////////////////////r////////
  // Range Rings
  /// ///////////////////////////////////////////////////////////////////////////
  toggleRangeRingsVisibility: thunk((actions, _, { getState }) => {
    const visible = !getState().areRingsVisible;
    actions.setAreRangeRingsVisible(visible);
    actions._setPointCloudGlobalSettings({ showRangeRings: visible });

    return getState().areRingsVisible;
  }),

  setAreRangeRingsVisible: action((state, isVisible) => {
    state.areRingsVisible = isVisible;
  }),

  toggleAngleRulersVisibility: thunk((actions, _, { getState }) => {
    const visible = !getState().twoDImageSettings.showImageRulers;
    actions.setAreAngleRulersVisible(visible);
    return visible;
  }),

  setAreAngleRulersVisible: action((state, isVisible) => {
    state.twoDImageSettings.showImageRulers = isVisible;
  }),

  initAvailableChannels: thunk((actions, _, { getState }) => {
    const { imageObservable, pointCloudGlobalSettings, twoDImageSettings } =
      getState();
    const { selectedImagePanelSn, imageSettings } = twoDImageSettings;
    const sub = imageObservable
      .pipe(take(1))
      .subscribe((message: DataMessage) => {
        if (!message) return;
        // All channels from message
        const availableChannels = message.availableChannels || [];
        actions.setAvailableChannels(availableChannels);
        const validModes = availableChannels.filter(
          (m) => !EXCLUDED_COLOR_MODES.includes(m as any)
        );
        const rgbChannels = message.rgbChannels || [];
        actions.setRgbChannels(rgbChannels);
        const rgbAvailable = validModes.includes(
          COLOR_MODE.RGB as unknown as string
        );
        const validPointModes = rgbAvailable
          ? validModes
          : validModes.filter((m) => m !== COLOR_MODE.RGB);
        const orderedValidPointModes = sortChannels(validPointModes);
        actions.setAvailablePointColorModes(orderedValidPointModes);
        actions.updatePointCloudGlobalSettings({
          colorModeOptions: orderedValidPointModes,
        });

        const validImageModes = availableChannels.filter(
          (m) => !EXCLUDED_IMAGE_COLOR_MODES.includes(m as any)
        );
        actions.setAvailableImageColorModes(sortChannels(validImageModes));
        // Add/remove RGB option from color palette options
        const withRgbPalettes = Array.from(
          new Set([
            ...getState().pointCloudGlobalSettings.colorPaletteOptions,
            'RGB',
          ])
        );
        const withoutRgbPalettes =
          getState().pointCloudGlobalSettings.colorPaletteOptions.filter(
            (p) => p !== 'RGB'
          );
        actions.updatePointCloudGlobalSettings({
          colorPaletteOptions: (rgbChannels.length > 0
            ? withRgbPalettes
            : withoutRgbPalettes) as ColorPaletteName[],
        });

        const prevMode = pointCloudGlobalSettings.coloringMode;
        let chosenMode: string | undefined;
        if (rgbAvailable) {
          chosenMode = COLOR_MODE.RGB;
        } else if (rgbChannels.length > 0) {
          const [firstRgbChannel] = rgbChannels;
          chosenMode = firstRgbChannel;
        } else if (prevMode && validPointModes.includes(prevMode)) {
          chosenMode = prevMode;
        } else if (validPointModes.includes(COLOR_MODE.REFLECTIVITY)) {
          chosenMode = COLOR_MODE.REFLECTIVITY;
        } else {
          // eslint-disable-next-line prefer-destructuring
          chosenMode = validPointModes[0];
        }
        let chosenPalette: ColorPaletteName;
        if (rgbChannels.length > 0 && rgbChannels.includes(chosenMode)) {
          chosenPalette = 'RGB';
        } else if (chosenMode?.startsWith(COLOR_MODE.REFLECTIVITY)) {
          chosenPalette = 'CalRef';
        } else {
          chosenPalette = 'Ouster' as ColorPaletteName;
        }

        actions.setPointColorMode(chosenMode as any);
        actions.updatePointCloudGlobalSettings({
          coloringMode: chosenMode as any,
        });
        actions.setPointColorPalette(chosenPalette);
        actions.updatePointCloudGlobalSettings({ colorPalette: chosenPalette });

        const firstAuto = false;
        const firstScaler = !(
          chosenMode.startsWith(COLOR_MODE.REFLECTIVITY) ||
          (rgbChannels.length > 0 && rgbChannels.includes(chosenMode))
        );
        const firstNoise = chosenMode === COLOR_MODE.NEAR_IR;

        actions._setPointCloudAutoLevel(firstAuto);
        actions.updatePointCloudGlobalSettings({ isAutoLevelsOn: firstAuto });
        actions.updatePointCloudGlobalSettings({
          isNoiseSuppressionOn: firstNoise,
        });

        const pcSensorsSettings = { ...getState().pointCloudSensorsSettings };
        const availableReturns = getAvailableReturns({
          colorMode: chosenMode as any,
          actualChannels: availableChannels,
        });
        Object.keys(pcSensorsSettings).forEach((sn) => {
          if (availableReturns.length === 1) {
            pcSensorsSettings[sn].returnsSettings = [
              { visible: true, highlightColor: null },
            ];
          } else if (availableReturns.length === 2) {
            pcSensorsSettings[sn].returnsSettings = [
              { visible: true, highlightColor: null },
              { visible: true, highlightColor: null },
            ];
          } else {
            pcSensorsSettings[sn].returnsSettings = [];
          }
          cachedSensorReturnNumbers[sn] = availableReturns.map(
            (_ret, idx) => idx
          );
          cachedColorerReturns[sn] = availableReturns.map((_ret, idx) => ({
            index: idx,
            highlightColor: null,
          }));
        });
        actions._setPointCloudSensorsSettings(pcSensorsSettings);

        getState().pcAutoLeveler = new PointcloudAutoLeveler(
          chosenMode as any,
          firstAuto
        );
        getState().pcScaler = new PointcloudScaler(
          chosenMode as any,
          firstScaler
        );
        getState().pcNoiseSuppressor = new PointcloudNoiseSuppressor(
          chosenMode as any,
          firstNoise
        );

        const paletteKey = chosenPalette;
        const colorMapToUse =
          allColorPalettes[paletteKey] ??
          (paletteKey === 'RGB'
            ? allColorPalettes.RGB
            : allColorPalettes.Ouster);
        const seeded = { ...getState().pcColorers };
        const isRgb =
          rgbChannels.length > 0 && rgbChannels.includes(chosenMode);
        Object.keys(pcSensorsSettings).forEach((sn) => {
          const col =
            seeded[sn] ??
            new PointcloudColorer(chosenMode as any, colorMapToUse, isRgb);
          col.channelField = chosenMode as any;
          col.colorMap = colorMapToUse;
          col.whichReturns = cachedColorerReturns[sn]?.length
            ? cachedColorerReturns[sn]
            : [{ index: 0, highlightColor: null }];
          seeded[sn] = col;
        });
        actions._setPcColorers(seeded);

        if (
          imageSettings &&
          selectedImagePanelSn &&
          imageSettings[selectedImagePanelSn]
        ) {
          const sensorImageSettings = imageSettings[selectedImagePanelSn];
          const first = sensorImageSettings[0];
          first.colorModeOptions = validImageModes;
          if (rgbChannels.length > 0) {
            if (rgbAvailable) {
              first.coloringMode = COLOR_MODE.RGB;
            } else {
              const [firstRgbChannel] = rgbChannels;
              first.coloringMode = firstRgbChannel;
            }

            if (!first.colorPaletteOptions.includes('RGB')) {
              first.colorPaletteOptions = [...first.colorPaletteOptions, 'RGB'];
            }
            first.colorPalette = 'RGB';
            first.isAutoLevelsOn = false;
            first.isAutoLevelsDisabled = true;
            first.isNoiseSuppressionOn = false;
            first.isNoiseSuppressionDisabled = true;
          } else {
            if (first.colorPaletteOptions.includes('RGB')) {
              first.colorPaletteOptions = first.colorPaletteOptions.filter(
                (p: string) => p !== 'RGB'
              );
            }
            if (
              first.coloringMode === COLOR_MODE.RGB ||
              !validImageModes.includes(first.coloringMode) ||
              (rgbChannels.length > 0 &&
                rgbChannels.includes(first.coloringMode))
            ) {
              const fallbackImageMode = extractDefaultColorSetting(
                validImageModes,
                DEFAULT_IMAGE_COLOR_MODES,
                'No available image color modes yet'
              );
              first.coloringMode = fallbackImageMode;
            }
            if (first.colorPalette === 'RGB') first.colorPalette = 'Greyscale';

            // Ensure initial palette is CalRef when Reflectivity is selected
            if (first.coloringMode.startsWith(COLOR_MODE.REFLECTIVITY)) {
              first.colorPalette = 'CalRef';
            }

            first.isAutoLevelsDisabled =
              (first.coloringMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
                first.colorPalette === 'CalRef') ||
              (rgbChannels.length > 0 &&
                rgbChannels.includes(first.coloringMode));
            first.isAutoLevelsOn = !first.isAutoLevelsDisabled;
            first.isNoiseSuppressionDisabled =
              first.coloringMode !== COLOR_MODE.NEAR_IR;
            first.isNoiseSuppressionOn =
              first.coloringMode === COLOR_MODE.NEAR_IR;
          }
          const availImgReturns = getAvailableReturns({
            colorMode: first.coloringMode,
            actualChannels: availableChannels,
          });
          if (availImgReturns.length === 1) {
            first.returnsSettings = [{ visible: true, disabled: true }];
          } else if (availImgReturns.length === 2) {
            if (
              !Array.isArray(first.returnsSettings) ||
              first.returnsSettings.length !== 2
            ) {
              first.returnsSettings = [
                { visible: true, disabled: false },
                { visible: false, disabled: false },
              ];
            } else {
              first.returnsSettings[0].disabled = false;
              first.returnsSettings[1].disabled = false;
              if (
                !first.returnsSettings[0].visible &&
                !first.returnsSettings[1].visible
              ) {
                first.returnsSettings[0].visible = true;
              }
            }
          } else {
            first.returnsSettings = [];
          }
          actions.updateTwoDImageSettings({
            imageSettings: {
              ...imageSettings,
              [selectedImagePanelSn]: sensorImageSettings,
            },
          });
        }

        actions.updateDataMessage(
          getState().dataMessage as Subject<DataMessage[]>
        );

        const shouldFlipAuto =
          !(rgbChannels.length > 0 && rgbChannels.includes(chosenMode)) &&
          !(
            chosenMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
            chosenPalette === 'CalRef'
          );
        if (shouldFlipAuto) {
          const once = getState().pointCloudObservable?.pipe(take(1));
          if (once) {
            const flip = once.subscribe({
              next: () => {
                actions.updatePointCloudGlobalSettings({
                  isAutoLevelsOn: true,
                });
                actions._setPointCloudAutoLevel(true);
                const st = getState();
                st.pcAutoLeveler.isActive = true;
                if (!chosenMode?.startsWith(COLOR_MODE.REFLECTIVITY)) {
                  st.pcScaler.isActive = false;
                }
                actions.updateDataMessage(
                  st.dataMessage as Subject<DataMessage[]>
                );
                flip.unsubscribe();
              },
            });
          }
        }
      });
    return () => sub.unsubscribe();
  }),

  // unification
  setLeftSidebarExpanded: action((state, isExpanded) => {
    state.leftSidebarExpanded = isExpanded;
  }),

  setFlipped: action((state, isFlipped) => {
    state.flipped = isFlipped;
  }),

  _setTwoDImageSettings: action((state, partialNewSettings) => {
    // Merge new settings with existing ones
    const newState = { ...state.twoDImageSettings, ...partialNewSettings };
    if (partialNewSettings.imageSettings) {
      newState.imageSettings = {
        ...(state.twoDImageSettings.imageSettings ?? {}), // Keep existing sensors
        ...partialNewSettings.imageSettings, // Overwrite/add sensors from partial update
      };
    }

    state.twoDImageSettings = newState;
  }),

  updateTwoDImageSettings: thunk(
    (actions, partialNewSettings, { getState }) => {
      const {
        metadata, // This is SensorMetadata[]
        imageDestaggerer,
        imageScaler,
        imageSuppressor,
        imageAutoLeveler,
        twoDImageSettings, // Current state { imageSettings: Record<>, selectedImagePanelSn, ... }
        imageObservable, // Base observable from updateDataMessage
        previousImageColorPalette,
        rgbChannels,
      } = getState();

      if (!Array.isArray(metadata) || metadata.length === 0) {
        console.warn(
          'Cannot update 2D image settings: metadata not loaded or empty.'
        );
        return;
      }

      const tempStateForMerge = { ...twoDImageSettings };
      if (partialNewSettings.imageSettings) {
        // Ensure deep merge for the imageSettings record
        tempStateForMerge.imageSettings = {
          ...(twoDImageSettings.imageSettings ?? {}),
          ...partialNewSettings.imageSettings,
        };
        // Remove imageSettings from partialNewSettings to avoid double merging below
        delete partialNewSettings.imageSettings;
      }
      // Merge the rest of the partial settings (like showImagePanel, selectedImagePanelSn, etc.)
      const newImageSettingsState = {
        ...tempStateForMerge,
        ...partialNewSettings,
      };

      if (!newImageSettingsState.imageSettings) {
        newImageSettingsState.imageSettings = {};
      }

      const { imageSettings, selectedImagePanelSn } = newImageSettingsState;

      // If there is an existing destroy subject, signal cleanup.
      // if we don't clean up, every time when we select new image panel,
      // a new observable is created
      if (newImageSettingsState.imageObservableDestroy$) {
        newImageSettingsState.imageObservableDestroy$.next();
        newImageSettingsState.imageObservableDestroy$.complete();
      }

      // Create a new destroy subject for the new observables.
      const destroy$ = new Subject<void>();
      newImageSettingsState.imageObservableDestroy$ = destroy$;

      newImageSettingsState.imageObservables = [];

      if (selectedImagePanelSn && imageSettings[selectedImagePanelSn]) {
        const selectedSensorSettingsArray = imageSettings[selectedImagePanelSn];

        // Normalize only the default Greyscale to CalRef for Reflectivity on sensor switch
        selectedSensorSettingsArray.forEach((setting) => {
          if (
            typeof setting.coloringMode === 'string' &&
            setting.coloringMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
            setting.colorPalette === 'Greyscale'
          ) {
            setting.colorPalette = 'CalRef';
            setting.isAutoLevelsDisabled = true;
            setting.isAutoLevelsOn = false;
          }
        });

        const selectedSensorMetadata = metadata.find(
          (m) => m?.productSn === selectedImagePanelSn
        );

        if (!selectedSensorMetadata) {
          console.warn(
            `Metadata not found for selected sensor ${selectedImagePanelSn} during update.`
          );
          // Reset operators and clear observables if metadata is missing
          imageDestaggerer.channelFields = [];
          imageScaler.channelFields = [];
          imageAutoLeveler.channelFields = [];
          imageSuppressor.channelFields = [];
          newImageSettingsState.imageObservables = [];
        } else {
          // --- Filter to get only visible settings for the SELECTED sensor ---
          const selectedVisibleSettings = selectedSensorSettingsArray.filter(
            (s) => s.showImage
          );

          // we should only disable auto level for COLOR_MODE.REFLECTIVITY / CalRef
          // which shows calibrated reflectivity and for the other REFLECTIVITY palettes
          // autoLevel should be enabled to keep consistent with SDK
          selectedVisibleSettings.forEach((setting) => {
            if (
              rgbChannels.length > 0 &&
              rgbChannels.includes(setting.coloringMode)
            ) {
              if (!setting.colorPaletteOptions.includes('RGB')) {
                setting.colorPaletteOptions = [
                  ...setting.colorPaletteOptions,
                  'RGB',
                ];
              }
              setting.colorPalette = 'RGB';
              setting.isAutoLevelsOn = false;
              setting.isNoiseSuppressionOn = false;
              setting.isAutoLevelsDisabled = true;
              setting.isNoiseSuppressionDisabled = true;
            } else {
              if (setting.colorPaletteOptions.includes('RGB')) {
                setting.colorPaletteOptions =
                  setting.colorPaletteOptions.filter(
                    (p: string) => p !== 'RGB'
                  );
              }
              if (setting.coloringMode?.startsWith(COLOR_MODE.REFLECTIVITY)) {
                if (setting.colorPalette === 'CalRef') {
                  setting.isAutoLevelsDisabled = true;
                  setting.isAutoLevelsOn = false;
                } else {
                  setting.isAutoLevelsDisabled = false;
                  setting.isAutoLevelsOn =
                    previousImageColorPalette === 'CalRef'
                      ? true
                      : setting.isAutoLevelsOn;
                }
                actions._setPreviousImageColorPalette(setting.colorPalette);
              } else if (setting.coloringMode === COLOR_MODE.NEAR_IR) {
                setting.isNoiseSuppressionDisabled = false;
              }
            }
          });

          // --- Configure Operators based on all visible images of the SELECTED sensor ---
          const visibleColorModes = Array.from(
            new Set(selectedVisibleSettings.map((s) => s.coloringMode))
          );
          const autoLevelModes = Array.from(
            new Set(
              selectedVisibleSettings
                .filter((s) => s.isAutoLevelsOn)
                .map((s) => s.coloringMode)
            )
          );
          const scalingModes = Array.from(
            new Set(
              visibleColorModes
                .filter((mode) => !autoLevelModes.includes(mode))
                .filter((mode) => !mode.startsWith(COLOR_MODE.REFLECTIVITY)) // Don't scale raw reflectivity
            )
          );
          const noiseModes = Array.from(
            new Set(
              selectedVisibleSettings
                .filter((s) => s.isNoiseSuppressionOn)
                .map((s) => s.coloringMode)
            )
          );

          imageDestaggerer.channelFields = visibleColorModes;
          imageScaler.channelFields = scalingModes;
          imageAutoLeveler.channelFields = autoLevelModes;
          imageSuppressor.channelFields = noiseModes;

          selectedSensorSettingsArray.forEach((setting) => {
            // Allow duplicates: do not exclude modes already used
            const colorModeOptions = getState().availableImageColorModes;
            setting.colorModeOptions = colorModeOptions;

            // Update returnsSettings based on the selected sensor's profile and the setting's color mode
            const availableReturns = getAvailableReturns({
              colorMode: setting.coloringMode,
              actualChannels: getState().availableChannels,
            });

            if (availableReturns.length === 1) {
              setting.returnsSettings = [{ visible: true, disabled: true }]; // Disable if only one option
            } else if (availableReturns.length === 2) {
              // Preserve existing visibility if possible, otherwise default
              if (
                !Array.isArray(setting.returnsSettings) ||
                setting.returnsSettings.length !== 2
              ) {
                setting.returnsSettings = [
                  { visible: true, disabled: false },
                  { visible: false, disabled: false },
                ];
              } else {
                if (
                  !setting.returnsSettings[0].visible &&
                  !setting.returnsSettings[1].visible
                ) {
                  setting.returnsSettings[0].visible = true;
                }
                setting.returnsSettings[0].disabled = false;
                setting.returnsSettings[1].disabled = false;
              }
            } else {
              // No returns available for this mode? Clear settings.
              setting.returnsSettings = [];
            }
          });

          // --- Create observables array for visible images of the SELECTED sensor ---
          newImageSettingsState.imageObservables = selectedVisibleSettings
            .map((visibleImageSetting) => {
              const { coloringMode, colorPalette, returnsSettings } =
                visibleImageSetting;
              const visibleReturnIndex = returnsSettings.findIndex(
                ({ visible }) => visible
              );
              const isRgb =
                rgbChannels.length > 0 && rgbChannels.includes(coloringMode);
              const imageColorer = getCachedImageColorer(
                coloringMode,
                colorPalette,
                visibleReturnIndex > -1 ? [visibleReturnIndex] : [],
                isRgb
              );

              if (!imageObservable) return NEVER; // Handle case where base observable isn't ready
              return imageObservable.pipe(
                // Unsubscribe when sensor changes.
                takeUntil(destroy$),
                // Filter messages to only process those from the selected sensor
                filterDataMessagesBySensorSN(selectedImagePanelSn),

                map((message: DataMessage) => imageColorer.colorImage(message)),
                catchError((err) => {
                  console.error(
                    `Error in image observable pipeline for ${selectedImagePanelSn}, mode ${coloringMode}:`,
                    err
                  );
                  return NEVER;
                }),
                share({
                  resetOnRefCountZero: false,
                })
              );
            })
            .filter((obs) => obs !== NEVER); // Filter out NEVER and assert type
        }
      } else {
        // Reset operators if no sensor is selected
        imageDestaggerer.channelFields = [];
        imageScaler.channelFields = [];
        imageAutoLeveler.channelFields = [];
        imageSuppressor.channelFields = [];
        // Ensure observables array is empty
        newImageSettingsState.imageObservables = [];
      }

      // --- Update the state ---
      actions._setTwoDImageSettings(newImageSettingsState);

      // Recreate observables to use updated image operators
      actions.recreateObservables();
    }
  ),

  setSelectedImagePanelSn: thunk((actions, sn) => {
    actions._setSelectedImagePanelSn(sn);
    actions.updateTwoDImageSettings({});
  }),

  swapImages: thunk((actions, { sensorSn, indexA, indexB }, { getState }) => {
    const { imageSettings } = getState().twoDImageSettings;

    // Check if settings exist for the target sensor
    if (!imageSettings || !imageSettings[sensorSn]) {
      console.error(
        `swapImages: Settings not found for sensor SN: ${sensorSn}`
      );
      return;
    }

    const sensorImageSettings = imageSettings[sensorSn];

    if (
      indexA < 0 ||
      indexA >= sensorImageSettings.length ||
      indexB < 0 ||
      indexB >= sensorImageSettings.length ||
      indexA === indexB // No need to swap if indices are the same
    ) {
      console.error(
        `swapImages: Invalid indices [${indexA}, ${indexB}] for sensor ${sensorSn} with ${sensorImageSettings.length} settings.`
      );
      return;
    }

    const newSensorImageSettings = [...sensorImageSettings];
    [newSensorImageSettings[indexA], newSensorImageSettings[indexB]] = [
      newSensorImageSettings[indexB],
      newSensorImageSettings[indexA],
    ];

    const newImageSettingsRecord = {
      ...imageSettings,
      [sensorSn]: newSensorImageSettings,
    };

    actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });

    actions.updateTwoDImageSettings({});
  }),

  addImage: thunk((actions, sensorSn, { getState }) => {
    const { metadata, availableImageColorModes, twoDImageSettings } =
      getState();
    const { imageSettings } = twoDImageSettings; // Don't need selectedImagePanelSn here

    if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
      console.warn(`addImage: Settings not found for sensor SN: ${sensorSn}`);
      return;
    }
    if (!Array.isArray(metadata) || metadata.length === 0) {
      console.warn('addImage: Metadata not available.');
      return;
    }

    const targetSensorMetadata = metadata.find(
      (m) => m?.productSn === sensorSn
    );
    if (!targetSensorMetadata) {
      console.warn(
        `addImage: Metadata not found for target sensor ${sensorSn}.`
      );
      return;
    }

    const currentSensorSettings = imageSettings[sensorSn];
    const udpProfileLidar =
      extractLidarProfileFromMetadata(targetSensorMetadata);

    const twoDImageOptionsToAdd = calculateDynamicImageOptions(
      udpProfileLidar,
      twoDImageOptionsToAddBase as Record<string, Partial<ImageSettings>>
    );

    const isSignalChannelFieldAvailable = availableImageColorModes.includes(
      COLOR_MODE.SIGNAL
    );

    // Exclude coloringModes already used by this sensor (so we add a new mode)
    const usedColorModes = new Set(
      (currentSensorSettings || []).map((s) => s.coloringMode)
    );

    const candidates = Object.values(twoDImageOptionsToAdd);

    // First try: any candidate that's allowed (signal availability) and not used yet
    const filtered = candidates.filter(
      (c) =>
        (isSignalChannelFieldAvailable ||
          c.coloringMode !== COLOR_MODE.SIGNAL) &&
        !usedColorModes.has(c.coloringMode)
    );

    let imageToAdd = filtered[0];

    // Second try: any candidate not used yet (allow SIGNAL if necessary)
    if (!imageToAdd) {
      imageToAdd =
        candidates.find((c) => !usedColorModes.has(c.coloringMode)) ||
        // Final fallback: prefer non-SIGNAL if SIGNAL isn't available, else first candidate
        (isSignalChannelFieldAvailable
          ? candidates[0]
          : candidates.find((c) => c.coloringMode !== COLOR_MODE.SIGNAL) ||
            candidates[0]);
    }

    if (!imageToAdd) {
      console.warn(
        `addImage: Could not determine an image setting to add for sensor ${sensorSn}.`
      );
      return;
    }

    const newSensorSettings = [...currentSensorSettings, imageToAdd];
    const newImageSettingsRecord = {
      ...imageSettings,
      [sensorSn]: newSensorSettings, // Update the specific sensor's array
    };

    actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });
    actions.updateTwoDImageSettings({});
  }),

  deleteImage: thunk((actions, { sensorSn, index }, { getState }) => {
    const { imageSettings } = getState().twoDImageSettings;

    if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
      console.error(
        `deleteImage: Settings not found for sensor SN: ${sensorSn}`
      );
      return;
    }
    const currentSensorSettings = imageSettings[sensorSn];
    if (index < 0 || index >= currentSensorSettings.length) {
      console.error(
        `deleteImage: Invalid index ${index} for sensor ${sensorSn}`
      );
      return;
    }
    // Prevent deleting the last image panel for a sensor
    if (currentSensorSettings.length <= 1) {
      console.warn(
        `deleteImage: Cannot delete the last image panel for sensor ${sensorSn}.`
      );
      return;
    }

    const newSensorSettings = currentSensorSettings.filter(
      (_, i) => i !== index
    );

    // Create the new record with the updated array
    const newImageSettingsRecord = {
      ...imageSettings,
      [sensorSn]: newSensorSettings,
    };

    // Update state and trigger recalculations
    actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });
    actions.updateTwoDImageSettings({});
  }),

  updateImage: thunk(
    (actions, { sensorSn, index, partialSettings }, { getState }) => {
      const { metadata, twoDImageSettings, rgbChannels } = getState();
      const { imageSettings } = twoDImageSettings;

      if (!sensorSn || !imageSettings || !imageSettings[sensorSn]) {
        console.error(
          `updateImage: Settings not found for sensor SN: ${sensorSn}`
        );
        return;
      }
      const currentSensorSettings = imageSettings[sensorSn];
      if (index < 0 || index >= currentSensorSettings.length) {
        console.error(
          `updateImage: Invalid index ${index} for sensor ${sensorSn}`
        );
        return;
      }
      if (!Array.isArray(metadata) || metadata.length === 0) {
        console.warn('updateImage: Metadata not available.');
        return;
      }
      const targetSensorMetadata = metadata.find(
        (m) => m?.productSn === sensorSn
      );
      if (!targetSensorMetadata) {
        console.warn(
          `updateImage: Metadata not found for target sensor ${sensorSn}.`
        );
        return;
      }

      const udpProfileLidar =
        extractLidarProfileFromMetadata(targetSensorMetadata);
      const dynamicOptionsMap = calculateDynamicImageOptions(
        udpProfileLidar,
        twoDImageOptionsToAddBase as Record<string, Partial<ImageSettings>>
      );

      const prevImageSettings = currentSensorSettings[index];
      const newColoringMode =
        partialSettings.coloringMode ?? prevImageSettings.coloringMode;
      const coloringModeChanged =
        newColoringMode !== prevImageSettings.coloringMode;

      // Palette selection:
      // - Force CalRef when switching INTO Reflectivity (initial default),
      // - When switching AWAY from Reflectivity, map CalRef -> Greyscale.
      // - Otherwise preserve previous or use explicit user palette if provided.
      let newColorPalette: ColorPaletteName | string | undefined;
      if (
        coloringModeChanged &&
        typeof newColoringMode === 'string' &&
        newColoringMode.startsWith(COLOR_MODE.REFLECTIVITY)
      ) {
        newColorPalette = 'CalRef';
      } else if (
        coloringModeChanged &&
        !newColoringMode.startsWith(COLOR_MODE.REFLECTIVITY)
      ) {
        newColorPalette =
          prevImageSettings.colorPalette === 'CalRef'
            ? 'Greyscale'
            : prevImageSettings.colorPalette;
      } else if (partialSettings.colorPalette) {
        newColorPalette = partialSettings.colorPalette;
      } else {
        newColorPalette = prevImageSettings.colorPalette;
      }

      let baseSettingsForUpdate: ImageSettings;
      if (coloringModeChanged) {
        // If mode changed, start with the defaults for the *new* mode from our dynamic map
        baseSettingsForUpdate =
          dynamicOptionsMap[newColoringMode] ?? defaultImageSettings;
      } else {
        // If mode didn't change, start with the *previous* settings
        baseSettingsForUpdate = prevImageSettings;
      }

      const finalUpdatedSetting: ImageSettings = {
        ...baseSettingsForUpdate,
        ...partialSettings,
        coloringMode: newColoringMode,
        colorPalette: newColorPalette as ColorPaletteName,
        isAutoLevelsDisabled:
          (newColoringMode?.startsWith(COLOR_MODE.REFLECTIVITY) &&
            newColorPalette === 'CalRef') ||
          (rgbChannels.length > 0 && rgbChannels.includes(newColoringMode)),
        isAutoLevelsOn: coloringModeChanged
          ? !(
              newColoringMode.startsWith(COLOR_MODE.REFLECTIVITY) &&
              newColorPalette === 'CalRef'
            )
          : partialSettings.isAutoLevelsOn ?? prevImageSettings.isAutoLevelsOn,
        isNoiseSuppressionOn: coloringModeChanged
          ? newColoringMode === COLOR_MODE.NEAR_IR
          : partialSettings.isNoiseSuppressionOn ??
            prevImageSettings.isNoiseSuppressionOn,
        isNoiseSuppressionDisabled: newColoringMode !== COLOR_MODE.NEAR_IR,
      };

      const updatedSensorImages = [
        ...currentSensorSettings.slice(0, index),
        finalUpdatedSetting,
        ...currentSensorSettings.slice(index + 1),
      ];

      const newImageSettingsRecord = {
        ...imageSettings,
        [sensorSn]: updatedSensorImages,
      };

      actions._setTwoDImageSettings({ imageSettings: newImageSettingsRecord });
      actions.updateTwoDImageSettings({
        imageSettings: newImageSettingsRecord,
      });
    }
  ),
};
